import * as icons from '@mui/icons-material';
import { Autocomplete, Box, IconButton, MenuItem, Stack, TextField } from '@mui/material';
import type { GridColDef, GridSingleSelectColDef } from '@mui/x-data-grid';
import { useMemo } from 'react';

export type Primitive = string | number | boolean;

export const TEXT_OPERATORS = [
  'contains',
  'startswith',
  'endswith',
  'eq',
  'ne',
  'null',
  'nnull',
] as const;
export type TextOperator = (typeof TEXT_OPERATORS)[number];
export const NUMERIC_OPERATORS = ['eq', 'ne', 'lt', 'lte', 'gt', 'gte', 'null', 'nnull'] as const;
export type NumericOperator = (typeof NUMERIC_OPERATORS)[number];
export const DATETIME_OPERATORS = ['lte', 'gte', 'null', 'nnull'] as const;
export type DateTimeOperator = (typeof DATETIME_OPERATORS)[number];
export const SELECT_OPERATORS = ['in', 'null', 'nnull'] as const;
export type SelectOperator = (typeof SELECT_OPERATORS)[number];

type Operator = TextOperator | NumericOperator | SelectOperator;

export const operatorRequiresValue = (operator: Operator): boolean => {
  switch (operator) {
    case 'null':
    case 'nnull':
      return false;
    default:
      return true;
  }
};

const OPERATOR_LABELS: Partial<Record<Operator, string>> = {
  eq: 'に一致する',
  ne: 'に一致しない',
  contains: 'を含む',
  startswith: 'で始まる',
  endswith: 'で終わる',
  in: 'に該当する',
  lt: 'より小さい（前）',
  lte: '以下（以前）',
  gt: 'より大きい（後）',
  gte: '以上（以後）',
  null: '空である',
  nnull: '空でない',
};

type SelectFilterRow = GridSingleSelectColDef & {
  type: 'singleSelect';
  operator: SelectOperator;
  value?: Primitive[];
};

type BooleanFilterRow = GridColDef & {
  type: 'boolean';
  operator: 'eq';
  value: boolean;
};

type NumericFilterRow = GridColDef & {
  type: 'number';
  operator: NumericOperator;
  value?: number;
};

type DateFilterRow = GridColDef & {
  type: 'date' | 'dateTime';
  operator: DateTimeOperator;
  value?: Date;
};

type TextFilterRow = GridColDef & {
  type: 'string';
  operator: TextOperator;
  value?: string | number | boolean;
};

export type FilterRow =
  | TextFilterRow
  | NumericFilterRow
  | DateFilterRow
  | BooleanFilterRow
  | SelectFilterRow;

export function getDefaultRow({ type, ...def }: GridColDef): FilterRow {
  switch (type) {
    case 'string':
      return {
        ...def,
        type,
        operator: 'contains',
        value: undefined,
      };
    case 'number':
      return {
        ...def,
        type,
        operator: 'eq',
        value: undefined,
      };
    case 'date':
    case 'dateTime':
      return {
        ...def,
        type,
        operator: 'gte',
        value: undefined,
      };
    case 'boolean': {
      return {
        ...def,
        type: 'boolean',
        operator: 'eq',
        value: true,
      };
    }
    case 'singleSelect':
      return {
        ...def,
        type,
        operator: 'in',
        value: [],
      };
    default:
      throw new Error(`Unsupported column type: ${type}`);
  }
}

function getOperators(def: GridColDef): ReadonlyArray<Operator> {
  switch (def.type) {
    case 'string':
      return TEXT_OPERATORS;
    case 'number':
      return NUMERIC_OPERATORS;
    case 'date':
    case 'dateTime':
      return DATETIME_OPERATORS;
    case 'boolean':
      return ['eq'];
    case 'singleSelect':
      return SELECT_OPERATORS;
    default:
      throw new Error(`Unsupported column type: ${def.type}`);
  }
}

const FilterRowValueTextInput = ({
  row,
  onChange,
}: {
  row: TextFilterRow;
  onChange: (value: string) => void;
}) => {
  const value = typeof row.value === 'string' ? row.value : '';

  return (
    <TextField
      size="small"
      fullWidth
      label="値"
      type="text"
      value={value}
      onChange={(event) => {
        onChange(event.target.value);
      }}
    />
  );
};

const FilterRowValueNumberInput = ({
  row,
  onChange,
}: {
  row: NumericFilterRow;
  onChange: (value: number) => void;
}) => {
  return (
    <TextField
      size="small"
      fullWidth
      label="値"
      type="number"
      value={row.value ?? ''}
      onChange={(event) => {
        onChange(Number(event.target.value));
      }}
    />
  );
};

const FilterRowValueBooleanInput = ({
  row,
  onChange,
}: {
  row: BooleanFilterRow;
  onChange: (value: boolean) => void;
}) => {
  return (
    <TextField
      select
      size="small"
      fullWidth
      label="値"
      value={row.value ? 'true' : 'false'}
      onChange={(event) => {
        onChange(event.target.value === 'true');
      }}
    >
      <MenuItem value={'true'}>True</MenuItem>
      <MenuItem value={'false'}>False</MenuItem>
    </TextField>
  );
};

const isInvalidDate = (date: Date) => Number.isNaN(date.getTime());

const FilterRowValueDateInput = ({
  row,
  onChange,
}: {
  row: FilterRow & { type: 'date' | 'dateTime' };
  onChange: (value: Date | undefined) => void;
}) => {
  const type = row.type === 'date' ? 'date' : 'datetime-local';
  let value: string | undefined;

  if (row.value) {
    // <input type="datetime-local"> はISO文字列の一部しか受け付けずタイムゾーンが考慮されないため
    // タイムゾーンの影響を打ち消して文字列が正しくJSTになるようにする
    const d = new Date(Number(row.value) - row.value.getTimezoneOffset() * 60000);
    if (type === 'date') {
      value = d.toISOString().split('T')[0]; // YYYY-MM-DD
    } else {
      value = d.toISOString().slice(0, 16); // YYYY-MM-DDTHH:mm
    }
  }

  return (
    <TextField
      size="small"
      fullWidth
      label="値"
      type={type}
      value={value}
      onChange={(event) => {
        const date = new Date(event.target.value);

        // 日付をクリアすると空文字になる
        if (isInvalidDate(date)) {
          onChange(undefined);
        } else {
          onChange(date);
        }
      }}
      slotProps={{
        inputLabel: { shrink: true },
      }}
    />
  );
};

export const FilterRowValueSelectInput = ({
  row,
  onChange,
}: {
  row: SelectFilterRow;
  onChange: (value: Primitive[]) => void;
}) => {
  const { valueOptions, getOptionLabel, getOptionValue } = row;

  if (typeof valueOptions === 'function')
    throw new Error('FilterRowのselectフィルターはvalueOptionsが関数である必要があります');

  const options = useMemo(
    () =>
      valueOptions?.map((option) => ({
        label: getOptionLabel?.(option) ?? String(option),
        value: getOptionValue?.(option) ?? option,
      })) ?? [],
    [getOptionLabel, getOptionValue, valueOptions],
  );

  const optionMap = useMemo(
    () => new Map(options.map((option) => [option.value, option])),
    [options],
  );

  return (
    <Autocomplete
      options={options}
      value={row.value?.map((v) => optionMap.get(v) ?? { label: String(v), value: v }) || []}
      filterSelectedOptions
      multiple
      size="small"
      fullWidth
      sx={{ width: '100%' }}
      onChange={(_, value) => {
        onChange(value.map((v) => v.value));
      }}
      getOptionLabel={(option) => option.label ?? String(option)}
      renderInput={(params) => <TextField {...params} label="値" size="small" fullWidth />}
    />
  );
};

export const FilterRowItem = ({
  row,
  defs,
  onUpdate,
  onDelete,
}: {
  row: FilterRow;
  defs: ReadonlyArray<GridColDef>;
  onUpdate: (updatedRow: FilterRow) => void;
  onDelete: () => void;
}) => {
  const operators = getOperators(row);
  const operator = row.operator ?? operators[0];

  const valueInput = useMemo(() => {
    const effectiveOperator = row.operator ?? operators[0];
    if (!operatorRequiresValue(effectiveOperator)) return null;

    switch (row.type) {
      case 'string':
        return (
          <FilterRowValueTextInput
            row={row}
            onChange={(value) => {
              onUpdate({ ...row, value });
            }}
          />
        );
      case 'number':
        return (
          <FilterRowValueNumberInput
            row={row}
            onChange={(value) => {
              onUpdate({ ...row, value });
            }}
          />
        );
      case 'date':
      case 'dateTime':
        return (
          <FilterRowValueDateInput
            row={row}
            onChange={(value) => {
              onUpdate({ ...row, value });
            }}
          />
        );
      case 'boolean':
        return (
          <FilterRowValueBooleanInput
            row={row}
            onChange={(value) => {
              onUpdate({ ...row, value });
            }}
          />
        );
      case 'singleSelect':
        return (
          <FilterRowValueSelectInput
            row={row}
            onChange={(value) => {
              onUpdate({ ...row, value });
            }}
          />
        );
      default:
        return null;
    }
  }, [onUpdate, operators, row]);

  return (
    <Stack direction="row" spacing={1} sx={{ width: '100%', p: 1, pl: 0, overflowX: 'auto' }}>
      <IconButton onClick={onDelete}>
        <icons.CloseOutlined />
      </IconButton>

      <TextField
        select
        size="small"
        label="フィールド"
        sx={{ flex: 1, minWidth: 140 }}
        value={row.field}
        onChange={(event) => {
          const value = event.target.value;
          const def = defs.find((def) => def.field === value);
          if (def) {
            onUpdate(getDefaultRow(def));
          }
        }}
      >
        <MenuItem value="">-</MenuItem>
        {defs.map((def) => (
          <MenuItem key={def.field} value={def.field}>
            {def.headerName}
          </MenuItem>
        ))}
      </TextField>

      {valueInput ? <Box sx={{ flex: 1, minWidth: 140 }}>{valueInput}</Box> : null}

      <TextField
        select
        size="small"
        label="オペレーター"
        sx={{ flex: 1, minWidth: 140 }}
        value={operator}
        onChange={(event) => {
          const nextOperator = event.target.value as Operator;

          onUpdate({
            ...row,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            operator: nextOperator as any,
            ...(operatorRequiresValue(nextOperator) ? {} : { value: undefined }),
          });
        }}
      >
        {operators.map((operator) => (
          <MenuItem key={operator} value={operator}>
            {OPERATOR_LABELS[operator] ?? operator}
          </MenuItem>
        ))}
      </TextField>
    </Stack>
  );
};
